/**
 * Sky painting ideas — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_painting_ideas`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const skyPaintingIdeasPack: ToilePack = {
    slug: 'sky-painting-ideas',
    category: 'craft',
    name: 'Sky painting ideas',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'milkyway', label: 'Milky Way', subject: 'a canvas of the Milky Way across a night sky above dark mountain silhouettes'},
        {key: 'clouds', label: 'Pastel clouds', subject: 'a canvas of big pastel clouds in peach and lilac at golden hour'},
        {key: 'storm', label: 'Storm sky', subject: 'a canvas of a dark storm sky with a fork of lightning over a golden wheat field'},
    ],
    styles: [
        {key: 'easy', label: 'Easy acrylic', style: 'easy beginner acrylic with smooth wet blends and simple dark silhouettes', palette: 'glowing blended colors with white highlights', framing: 'The canvas on a wooden easel seen straight on, a plain pale wall behind'},
        {key: 'impasto', label: 'Thick impasto', style: 'thick impasto applied with a palette knife, the clouds and light built up in raised strokes', palette: 'saturated colors with thick white peaks', framing: 'The canvas leaning against a white wall on a light wooden floor'},
        {key: 'realism', label: 'Soft realism', style: 'soft realistic acrylic with fine gradients and glowing detail', palette: 'natural colors blended gently from dark to light', framing: 'The canvas lying flat on a wooden work table seen from directly above, brushes and a paint palette beside it'},
    ],
    directions: ['northern lights', 'rainbow', 'pink sky'],
    directionHint: 'try: northern lights',
    seo: {
        title: 'Sky Painting Ideas: Milky Way, Pastel Clouds, Storm',
        metaDescription: 'Nine sky painting ideas: the Milky Way over mountains, pastel clouds at golden hour and a storm over a wheat field, in three styles.',
        h1: 'Sky painting ideas: Milky Way, pastel clouds at golden hour, storm over a wheat field',
        tagline: 'Nine sky canvases, three moods, each in easy blended acrylic, thick impasto or soft realism.',
        intro: 'Skies are painted for color and space, the easiest subject to make glow. These nine show three: the Milky Way across a night sky above dark mountain silhouettes, big pastel clouds in peach and lilac at golden hour, and a dark storm sky with a fork of lightning over a golden wheat field. Type another sky, such as northern lights or a rainbow, and the same sky carries it.',
        useCases: [
            'a bedroom wall piece',
            'a calm picture above a sofa',
            'a gift for a night-sky lover',
            'a first blended-color canvas',
        ],
        faq: [
            {q: 'What is in the storm picture?', a: 'A dark sky with a fork of lightning above a golden wheat field.'},
            {q: 'How do impasto and soft realism differ?', a: 'Impasto builds the clouds and light in thick raised strokes. Soft realism uses fine gradients with little visible texture.'},
            {q: 'Is the Milky Way picture an astronomical photograph?', a: 'No. It is a painted canvas of the Milky Way, with the stars and dust made in paint.'},
            {q: 'Can I change the sky?', a: 'Yes. Type another sky and the three styles carry it.'},
        ],
    },
}
